import { Edge, useGraphInstance } from "@antv/xflow"
import { memo, useCallback, useEffect, useRef } from "react"
import { v4 as uuidv4 } from "uuid";
import { LineType, PapersizeOptions } from "../../constant"
import { PaperDirection, PaperSize, TempLineType, getFlowTemplate } from "@/store/flowsheet/flowsheet.slice"
import { useSelector } from "react-redux"


export const FlowTemplate = memo(() => {
  const template = useSelector(getFlowTemplate)
  const graph = useGraphInstance()
  const lineRef = useRef<Edge | null>(null)


  const createLine = useCallback(() => {
    const {size, direction, border} = template!
    if (!(size && border !== TempLineType.None)) {
      return;
    }
    const {width, height} = getPaperSize(size, direction)
    
    const points = [
      {x: width, y: 0},
      {x: width, y: height},
      {x: 0, y: height},
    ]
    const otherAttrs: any = {}
    if (border === TempLineType.Dashed) {
      otherAttrs.strokeDasharray = "4 1"
    }
    const edgeOption = {
      id: uuidv4(),
      data: {type: LineType.Template},
      source: {x: 0, y: 0},
      target: {x: 0, y: 0},
      shape: 'edge',
      vertices: points,
      attrs: {
        line: {
          stroke: '#222',
          strokeWidth: 2,
          targetMarker: null,
          ...otherAttrs,
        },
      },
    }
    const edge = graph!.addEdge(edgeOption)
    lineRef.current = edge
    edge.setAttrByPath('path/pointerEvents', 'none')
    const container = graph?.findView(edge)
    container?.setClass('noPrint')
  }, [template, graph])


  const updateLine = useCallback(() => {
    const {size, direction, border} = template!
    const edge = lineRef.current!;
    if (border === TempLineType.None) {
      graph?.removeCell(edge.id)
      lineRef.current = null
      return;
    } else {
      // update border
      if (border === TempLineType.Dashed) {
        edge.setAttrByPath('path/strokeDasharray', "4 1")
      } else if (border === TempLineType.Solid) {
        edge.removeAttrByPath('path/strokeDasharray')
      }
    }
    const edgeBox = edge.getBBox()
    const {width: boxW, height: boxH} = edgeBox
    const {width, height} = getPaperSize(size!, direction)
    if (boxW !== width || boxH !== height) {
      const points = [
        {x: width, y: 0},
        {x: width, y: height},
        {x: 0, y: height},
      ]
      edge.setVertices(points)
    }

  }, [template, graph])

  useEffect(() => {
    if (!graph) return;
    if (!template) return;
    if (!lineRef.current) {
      createLine()
      return
    }
    updateLine()

  }, [graph, template, createLine, updateLine])

  return null;
})

function getSize(sizeName: string) {
  return PapersizeOptions.find(item => item.value === sizeName)
}

function getPaperSize(size: PaperSize, direction: PaperDirection = PaperDirection.Landscape) {
  const sizeData = getSize(size)!
  let width = sizeData!.width
  let height = sizeData!.height
  if (direction === PaperDirection.Portrait) {
    width = sizeData!.height
    height = sizeData!.width
  }

  return {width, height}
}